<script setup>
import UpdateTransportDialog from '@/components/transport-summary-page/UpdateTransportDialog.vue'
import UpdateInvoiceDialog from '@/components/transport-summary-page/UpdateInvoiceDialog.vue'
import TransportSummaryFilter from '@/components/transport-summary-page/TransportSummaryFilter.vue'
import TransportSummaryExport from '@/components/transport-summary-page/TransportSummaryExport.vue'
import TransportSummaryCard from '@/components/transport-summary-page/TransportSummaryCard.vue'
import TransportItemCard from '@/components/transport-summary-page/TransportItemCard.vue'
import BaseAlert from '@/components/base/BaseAlert.vue'
import PaginationBase from '../components/PaginationBase.vue'
import BaseLoadingSpinner from '@/components/base/BaseLoadingSpinner.vue'

import { ref, computed, onMounted } from 'vue'
import { useStore } from '@/utils/VuexHelpers'
import { CARTON } from '@/constants/general'

const store = useStore()

const transportDialog = ref(null)
const invoiceDialog = ref(null)

const cartonHeaders = [
  { value: 'productname', text: 'Product Name', sortable: true },
  { value: 'productCode', text: 'Product Code', sortable: true },
  { value: 'producttype', text: 'Product Type', sortable: true },
  { value: 'weight', text: 'Weight (kg)', sortable: true },
  { value: 'cartons', text: 'Cartons', sortable: false }
]

const porkerHeaders = [
  { value: 'body_no', text: 'Body Number', sortable: true },
  { value: 'producttype', text: 'Product Type', sortable: true },
  { value: 'weight', text: 'Weight (kg)', sortable: true }
]

const transportList = computed(() => store.getters.transportList)
const transportListSorted = computed(() => store.getters.transportListSorted)
const transportSummary = computed(() => store.getters.transportSummary)
const totalTransport = computed(() => store.getters.totalTransport)
const perPageTransport = computed(() => store.getters.perPageTransport)
const pageNumberTransp = computed(() => store.getters.pageNumberTransp)
const perPageOptions = computed(() => store.getters.perPageOptions)
const loadingTransport = computed(() => store.getters.loadingTransport)
const filtersTransp = computed(() => store.getters.filtersTransp)
const freightFilterInfo = computed(() => store.getters.freightFilterInfo)
const freightCodeMap = computed(() => store.getters.freightCodeMap)
const freightNames = computed(() => store.getters.freightNames)
const loadingSuppliers = computed(() => store.getters.loadingSuppliers)

const singleFreight = computed(() => {
  if (filtersTransp.value.delivery_by.value) return true
  return false
})

const perPage = computed({
  get: () => perPageTransport.value,
  set: (value) => {
    store.commit('SET_PER_PAGE_TRANSPORT', value)
    store.dispatch('fetchTransport')
  }
})

const isCartonItem = (item) => {
  if (item.loaded_items.length && item.loaded_items[0].producttype === CARTON)
    return true
  return false
}

const updateFreight = (payload) => {
  transportDialog.value.edit(payload)
}

const updateInvoice = (payload) => {
  invoiceDialog.value.edit(payload)
}

const updatePageNr = (nr) => {
  store.commit('SET_PAGE_NR_TRANSP', nr - 1)
  store.dispatch('fetchTransport')
}

const freightCode = (deliveryIds) => {
  let arr = deliveryIds.split(',')
  const mapped = arr.map((el) => {
    if (el) {
      return freightCodeMap.value?.[el]?.code ?? ''
    }
  })
  return mapped.filter(Boolean).join('/')
}

const initTransportPage = async () => {
  await store.dispatch('fetchTransport')

  store.dispatch('fetchDestinations')
  store.dispatch('fetchFreight')
}

// ON CREATED
store.commit('SET_USING_VUETIFY', true)
store.commit('SET_ACTIVE', 'transport')
initTransportPage()

onMounted(() => {
  if (
    freightNames.value?.length === 0 &&
    loadingSuppliers.value !== 'freightnames'
  ) {
    store.dispatch('fetchFreightNames')
  }
})
</script>

<template>
  <v-app>
    <BaseAlert />

    <v-container fluid>
      <div class="wrapper">
        <TransportSummaryFilter />

        <div class="content-page mt-0">
          <v-row no-gutters class="my-2 align-center">
            <div class="page-title-box d-flex">
              <h4 class="page-title">Transport</h4>
            </div>
            <TransportSummaryExport
              class="ms-auto"
              :transport-list="transportListSorted"
              :summary="transportSummary"
              :single-freight="singleFreight"
              :freight-code="freightCode"
            />
          </v-row>

          <UpdateTransportDialog ref="transportDialog" />
          <UpdateInvoiceDialog ref="invoiceDialog" />

          <v-row class="mt-1 mb-3 align-center" no-gutters>
            <div class="d-flex align-center">
              Transport Summary:
              <span class="font-weight-bold ms-2">{{ totalTransport }}</span>
            </div>
            <div class="ms-auto me-2 d-flex align-center">
              Summaries per page:
            </div>
            <b-select
              v-model="perPage"
              :options="perPageOptions"
              class="d-inline-block w-auto mr-4"
            ></b-select>
            <PaginationBase
              :total="totalTransport"
              :perPage="perPageTransport"
              :current="pageNumberTransp + 1"
              @updatePage="updatePageNr"
            />
          </v-row>

          <v-card v-if="loadingTransport === 'list'" class="mb-4">
            <v-card-text class="text-center">
              <BaseLoadingSpinner text="Transport Summary" />
            </v-card-text>
          </v-card>

          <TransportSummaryCard
            v-else
            :summary="transportSummary"
            :transport-list="transportListSorted"
            :single-freight="singleFreight"
            :freight-filter-info="freightFilterInfo"
            :freight-code="freightCode"
          />

          <TransportItemCard
            v-for="item in transportList"
            :key="item.id"
            :item="item"
            :loading="loadingTransport === 'list'"
            :carton-headers="cartonHeaders"
            :porker-headers="porkerHeaders"
            :is-carton-item="isCartonItem(item)"
            :freight-code="freightCode"
            @update-freight="updateFreight"
            @update-invoice="updateInvoice"
          />
        </div>
      </div>
    </v-container>
  </v-app>
</template>

<style scoped>
p {
  margin-bottom: 0;
  line-height: 1.1;
}
</style>
